/**
 * Outbox store (R7.2) — React Context + useReducer.
 *
 * Responsibilities:
 *  - Hold the in-memory list of OutboxItems.
 *  - Persist state to filesystem after every mutation (cantir/outbox/queue.json).
 *  - Load persisted state from filesystem on app start.
 *  - Expose `enqueue`, `setStatus`, `removeItem`, and `setProcessing` actions.
 *  - Expose `isProcessing` so the worker and UI can see the lock.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import type { OutboxItem, OutboxItemStatus } from './outboxTypes';
import { readJson, writeJson } from '../platform/filesystem';

const QUEUE_PATH = 'cantir/outbox/queue.json';

// ─── State / Actions ──────────────────────────────────────────────────────────

interface OutboxState {
  items: OutboxItem[];
  /** Mutex flag — set to true by the worker while processing. */
  isProcessing: boolean;
  /** True once the persisted state has been loaded (prevents saving before loading). */
  hydrated: boolean;
}

type OutboxAction =
  | { type: 'HYDRATE'; items: OutboxItem[] }
  | { type: 'ENQUEUE'; item: OutboxItem }
  | { type: 'SET_STATUS'; id: string; status: OutboxItemStatus; error?: string }
  | { type: 'REMOVE'; id: string }
  | { type: 'SET_PROCESSING'; processing: boolean };

function reducer(state: OutboxState, action: OutboxAction): OutboxState {
  switch (action.type) {
    case 'HYDRATE':
      return { ...state, items: action.items, hydrated: true };

    case 'ENQUEUE':
      return { ...state, items: [...state.items, action.item] };

    case 'SET_STATUS':
      return {
        ...state,
        items: state.items.map((it) =>
          it.id === action.id
            ? {
                ...it,
                status: action.status,
                lastError: action.error,
                attempts:
                  action.status === 'FAILED' ? it.attempts + 1 : it.attempts,
              }
            : it,
        ),
      };

    case 'REMOVE':
      return { ...state, items: state.items.filter((it) => it.id !== action.id) };

    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.processing };

    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface OutboxContextValue {
  items: OutboxItem[];
  isProcessing: boolean;
  pendingCount: number;
  enqueue: (item: OutboxItem) => void;
  setStatus: (id: string, status: OutboxItemStatus, error?: string) => void;
  removeItem: (id: string) => void;
  setProcessing: (processing: boolean) => void;
}

const OutboxContext = createContext<OutboxContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function OutboxProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    items: [],
    isProcessing: false,
    hydrated: false,
  });

  // Load persisted queue on mount
  useEffect(() => {
    readJson<OutboxItem[]>(QUEUE_PATH)
      .then((items) => {
        dispatch({
          type: 'HYDRATE',
          // Reset any SYNCING items left from a crashed session back to PENDING
          items: (items ?? []).map((it) =>
            it.status === 'SYNCING' ? { ...it, status: 'PENDING' } : it,
          ),
        });
      })
      .catch(() => {
        dispatch({ type: 'HYDRATE', items: [] });
      });
  }, []);

  // Persist to filesystem after every mutation (once hydrated)
  useEffect(() => {
    if (!state.hydrated) return;
    // Only persist non-DONE items (DONE items are removed immediately by the worker)
    const toSave = state.items.filter((it) => it.status !== 'DONE');
    writeJson(QUEUE_PATH, toSave).catch(() => {
      // Storage failure is non-fatal; the in-memory state is still valid
    });
  }, [state.items, state.hydrated]);

  const enqueue = useCallback((item: OutboxItem) => {
    dispatch({ type: 'ENQUEUE', item });
  }, []);

  const setStatus = useCallback(
    (id: string, status: OutboxItemStatus, error?: string) => {
      dispatch({ type: 'SET_STATUS', id, status, error });
    },
    [],
  );

  const removeItem = useCallback((id: string) => {
    dispatch({ type: 'REMOVE', id });
  }, []);

  const setProcessing = useCallback((processing: boolean) => {
    dispatch({ type: 'SET_PROCESSING', processing });
  }, []);

  const pendingCount = useMemo(
    () => state.items.filter((it) => it.status === 'PENDING' || it.status === 'FAILED').length,
    [state.items],
  );

  const value = useMemo<OutboxContextValue>(
    () => ({
      items: state.items,
      isProcessing: state.isProcessing,
      pendingCount,
      enqueue,
      setStatus,
      removeItem,
      setProcessing,
    }),
    [state.items, state.isProcessing, pendingCount, enqueue, setStatus, removeItem, setProcessing],
  );

  return <OutboxContext.Provider value={value}>{children}</OutboxContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOutbox(): OutboxContextValue {
  const ctx = useContext(OutboxContext);
  if (!ctx) throw new Error('useOutbox must be inside OutboxProvider');
  return ctx;
}
